import { useStore } from '../hooks/useStore';
import { INPUT_FIELDS } from '@/lib/constants/foxway';
import useTranslation from '../hooks/useTranslation';
import TextInputAndLabel, {
  TextInputAndLabelProps,
} from '../components/TextInputAndLabel';
import CheckboxAndLabel from '../components/CheckboxAndLabel';

const InputField = ({
  name,
  ...props
}: Omit<
  TextInputAndLabelProps,
  'name' | 'id' | 'type' | 'pattern' | 'maxLength'
> & { name: keyof typeof INPUT_FIELDS }) => (
  <TextInputAndLabel
    name={name.toString()}
    id={INPUT_FIELDS[name].id}
    type={INPUT_FIELDS[name].type}
    pattern={INPUT_FIELDS[name].pattern}
    maxLength={INPUT_FIELDS[name].maxLength}
    {...props}
  />
);

const ContactForm = () => {
  const details = useStore((state) => state.details);
  const partner = useStore((state) => state.partner);
  const locale = useStore((state) => state.locale);
  const translations = useTranslation();

  const termsLink = locale && partner?.clientTerms?.[locale];
  const policyLink = locale && partner?.clientPolicy?.[locale];
  const delayedRegistration = partner?.featureToggles.delayedRegistration;
  const collectMarketingConsent =
    partner?.featureToggles.collectMarketingConsent;

  return (
    <section className="mb-4 sm:mb-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
        {!delayedRegistration && (
          <InputField
            name="iban"
            label={translations.iban}
            description={translations.ibanDescription}
            placeholder={translations.ibanPlaceholder}
            defaultValue={details['iban']}
            required
            className="md:col-span-4"
          />
        )}
        {!delayedRegistration && (
          <>
            <InputField
              name="firstName"
              label={translations.firstName}
              defaultValue={details['firstName']}
              required
              className="md:col-span-2"
            />
            <InputField
              name="lastName"
              label={translations.lastName}
              defaultValue={details['lastName']}
              required
              className="md:col-span-2"
            />
            <InputField
              name="address"
              label={translations.address}
              defaultValue={details['address']}
              required
              className="md:col-span-4"
            />
            <InputField
              name="postalCode"
              label={translations.postalCode}
              defaultValue={details['postalCode']}
              required
            />
            <InputField
              name="city"
              label={translations.city}
              defaultValue={details['city']}
              required
              className="md:col-span-3"
            />
          </>
        )}

        <InputField
          name="email"
          label={translations.email}
          defaultValue={details['email']}
          required
          className="md:col-span-4"
        />
        {termsLink && policyLink && (
          <CheckboxAndLabel
            id="checkbox-terms"
            className="md:col-span-4"
            required
          >
            {translations.termsAndConditions(termsLink, policyLink)}
          </CheckboxAndLabel>
        )}
        {collectMarketingConsent && (
          <>
            <CheckboxAndLabel
              name="marketingConsent"
              id="checkbox-marketingConsent"
              className="md:col-span-4 relative"
            >
              {translations.marketingConsent(partner.name)}
              <p className="text-xs">{translations.marketingUnsubscribe}</p>
            </CheckboxAndLabel>
          </>
        )}
      </div>
    </section>
  );
};

export default ContactForm;
